import React from "react";
import {
  UserPlus,
  FileCheck2,
  Link2,
  ListChecks,
  SlidersHorizontal,
  Split,
  LayoutDashboard,
  FileDown,
} from "lucide-react";
import Seo, { faqJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import PageHero from "@/components/ui-kit/PageHero";
import { Container, Section, Eyebrow } from "@/components/ui-kit/Section";
import { Reveal } from "@/components/motion/Reveal";
import FaqAccordion from "@/components/blocks/FaqAccordion";
import DualAudience from "@/components/blocks/DualAudience";
import Cta from "@/components/ui-kit/Cta";

const STEPS = [
  {
    icon: UserPlus,
    title: "Sign up and choose your account type",
    body: "Start as an individual contractor or as an agency. Each path is built for how you actually work, so you only see what is relevant to you.",
  },
  {
    icon: FileCheck2,
    title: "Accept the automation consent",
    body: "Before anything moves, you read and accept the consent in full. Nothing is buried. It states plainly that ETAP routes your set aside and never holds your money, and that you can withdraw consent at any time.",
  },
  {
    icon: Link2,
    title: "Link your bank with Plaid",
    body: "The connection is read only. ETAP reads transactions so it can detect income. It never sees or stores your bank login credentials.",
  },
  {
    icon: ListChecks,
    title: "Choose which accounts to track",
    body: "Pick the accounts where your income lands. Anything you do not select is ignored, so personal accounts stay out of scope.",
  },
  {
    icon: SlidersHorizontal,
    title: "Set your federal and state percentages",
    body: "You choose the numbers. ETAP does not give tax advice or pick a bracket for you. The percentage you set is the percentage that gets routed.",
  },
  {
    icon: Split,
    title: "Income arrives, ETAP splits and routes it",
    body: "When a deposit is detected, ETAP calculates your set aside and routes that amount toward federal and state taxes. The rest stays in your account.",
  },
  {
    icon: LayoutDashboard,
    title: "Watch everything on your dashboard",
    body: "See each deposit, the amount routed, the fee, and your running total. Every split and transfer is recorded and auditable.",
  },
  {
    icon: FileDown,
    title: "Export reports at quarter and year end",
    body: "When it is time to file, your reports are ready. Export what you need as CSV or PDF and hand it to whoever prepares your return.",
  },
];

const FAQ = [
  {
    q: "What if a deposit is not income?",
    a: "You can mark deposits that are not income, such as transfers between your own accounts or a refund, so nothing is routed on money that should not count.",
  },
  {
    q: "Can I pause it?",
    a: "Yes. You can pause routing at any time. While paused, ETAP keeps your dashboard current but moves no money until you resume.",
  },
  {
    q: "What happens if my bank link breaks?",
    a: "ETAP notifies you and pauses routing for that account until the link is restored, so nothing moves on stale data. Reconnecting takes a moment through Plaid.",
  },
  {
    q: "Can I withdraw consent?",
    a: "Always, in one click. Withdrawal takes effect immediately and no further money is routed until you choose to resume.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How ETAP Works | From linked bank to routed taxes"
        description="See the full ETAP flow: consent, read only Plaid link, choose accounts, set your percentages, then income is detected, split, and routed. Start free."
        path="/how-it-works"
        jsonLd={[
          faqJsonLd(FAQ),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "How It Works", path: "/how-it-works" },
          ]),
        ]}
      />

      <PageHero
        eyebrow="How it works"
        title="From your first deposit to a filed year, in one clear flow"
        subtitle="Set it up once. After that, ETAP handles every deposit the way you decided and keeps a record you can hand to whoever files your taxes."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Cta to="/get-started" variant="primary" size="lg" location="hiw-hero" withArrow testId="hiw-hero-cta">
            Start Free Setup
          </Cta>
          <Cta to="/pricing" variant="outline" size="lg" location="hiw-hero-pricing" testId="hiw-hero-pricing">
            See pricing
          </Cta>
        </div>
      </PageHero>

      {/* Steps timeline */}
      <Section className="bg-canvas pt-4">
        <Container>
          <ol className="relative mx-auto max-w-3xl">
            <span
              aria-hidden="true"
              className="absolute left-6 top-2 hidden h-[calc(100%-1rem)] w-px bg-etap-deep-100 sm:block"
            />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={Math.min(i * 0.04, 0.2)}>
                <li className="relative mb-6 flex gap-5 rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-sm sm:p-7">
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-etap-deep text-white">
                    <s.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-etap-primary">Step {i + 1}</p>
                    <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-etap-deep-800">{s.title}</h2>
                    <p className="mt-2 text-etap-deep-600">{s.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>

          {/* Consent shown in full */}
          <Reveal className="mx-auto mt-6 max-w-3xl">
            <div className="rounded-3xl border border-etap-mint-300 bg-etap-mint-100 p-7">
              <Eyebrow>The consent, in plain words</Eyebrow>
              <p className="mt-3 text-etap-deep-800">
                By turning on automation, you authorize ETAP to detect income on the accounts you select and to route
                the percentage you choose toward your federal and state estimated taxes through authorized payment
                rails. ETAP never holds your funds and is not a bank or custodian. Payment movement is authorized
                through Stripe. You can change your percentages, pause routing, or withdraw consent at any time, and
                withdrawal takes effect immediately.
              </p>
              <p className="mt-3 text-sm text-etap-deep-600">
                ETAP does not provide tax, legal, or accounting advice, and does not file taxes on your behalf.
              </p>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* FAQ subsection */}
      <Section className="bg-white pt-0">
        <Container>
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Eyebrow>Common questions</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-bold tracking-tight text-etap-deep-900">
                A few things people ask before they start
              </h2>
            </Reveal>
            <Reveal className="mt-8">
              <FaqAccordion items={FAQ} idPrefix="hiw-faq" />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="bg-canvas pt-0">
        <Container>
          <DualAudience location="hiw-dual" />
        </Container>
      </Section>
    </>
  );
}
